/**
 * RASHUV TECHNOLOGIES — Products & Verticals Master Configuration
 * =========================================================================
 * Single Source of Truth for all Rashuv business divisions, active products,
 * in-development platforms, and foundational technologies.
 *
 * NOTE: All product details, capabilities, and roadmap statuses are strictly
 * grounded in actual capabilities without unverified or inflated claims.
 * =========================================================================
 */

export const VERTICALS = [
  {
    id: 'med',
    name: 'RashuvMed',
    navLabel: '🩺 RashuvMed',
    badge: '🩺 Healthcare & Clinical Systems Division',
    icon: '🩺',
    tagline: 'Digital queue management, doctor cabin consultation screens, and patient communication tools for outpatient clinics.',
    themeClass: 'vertical-med',
    color: '#059669',
    tintBg: 'var(--med-tint-bg)',
    tintBorder: 'var(--med-tint-border)'
  },
  {
    id: 'biz',
    name: 'Enterprise Solutions',
    navLabel: '💼 Enterprise Solutions',
    badge: '💼 Enterprise Solutions & Custom B2B Division',
    icon: '💼',
    tagline: 'Custom B2B workflow engineering, enterprise integrations, and specialized operational infrastructure.',
    themeClass: 'vertical-biz',
    color: '#0284c7',
    tintBg: 'var(--biz-tint-bg)',
    tintBorder: 'var(--biz-tint-border)'
  },
  {
    id: 'tech',
    name: 'Tech & AI Labs',
    navLabel: '⚡ Tech Labs',
    badge: '💻 Core Technology & Architecture',
    icon: '⚡',
    tagline: 'Cloud-hosted infrastructure delivering real-time event synchronization and cross-device operational telemetry.',
    themeClass: 'vertical-tech',
    color: '#6366f1',
    tintBg: 'var(--tech-tint-bg)',
    tintBorder: 'var(--tech-tint-border)',
    isTech: true
  }
];

export const PRODUCTS = [
  // ── RASHUVMED (HEALTHCARE DIVISION) ──────────────────────────────────────
  {
    id: 'rmedq',
    verticalId: 'med',
    title: 'RMedQ',
    version: 'v2.7.2',
    icon: '🏥',
    iconSvg: '/icons/rmedq-icon.svg',
    logoSvg: '/icons/rmedq-logo.svg',
    iconBg: '#ecfdf5',
    iconColor: '#059669',
    iconBorder: '#a7f3d0',
    status: 'active',
    statusLabel: 'Flagship • Active Production',
    statusClass: 'status-active',
    subtitle: 'Smart Clinic & Hospital OPD Operating Platform',
    desc: 'Clinical outpatient management platform designed to organize waiting areas and coordinate doctor cabins, reception desks, and patients through Instant QR check-ins, Live Web Queue tracking, Smart TV display hall modes, and clinical queue velocity analytics.',
    features: [
      'Instant Mobile QR Code Patient Token Self-Registration',
      'Doctor Consultation Console with Vitals, Patient History & Prescription Workflow',
      'Live Queue Status Tracking for Patients with Estimated Wait Times',
      'Waiting Hall High-Definition Smart TV Display Mode',
      'Role-Based Access for Receptionists, Doctors, and Clinic Administrators',
      'Doctor Cabin Consultation Consoles & OPD Velocity Telemetry'
    ],
    tags: ['Instant QR Check-in', 'Doctor Cabin Console', 'Waiting Hall TV Mode', 'Digital Health Records', 'OPD Queue Telemetry'],
    colSpan: 12,
    hasLiveDemo: true,
    localUrl: 'http://localhost:8080/',
    qaUrl: 'https://qa-rmedq-rashuv.web.app',
    stagingUrl: 'https://staging-rmedq-rashuv.web.app',
    prodUrl: 'https://rmedq.rashuv.com',
    actionText: 'Launch Live App',
    previewType: 'rmedq-telemetry'
  },

  // ── ENTERPRISE & WORKFLOW DIVISION ──────────────────────────────────────
  {
    id: 'enterprise-workflows',
    verticalId: 'biz',
    title: 'Enterprise Workflow Systems',
    version: 'Custom B2B',
    icon: '💼',
    iconBg: '#f0fdf4',
    iconColor: '#16a34a',
    iconBorder: '#bbf7d0',
    status: 'active',
    statusLabel: 'Enterprise Solutions • Active Production',
    statusClass: 'status-active',
    subtitle: 'Tailored Digital Infrastructure & System Integration',
    desc: 'Scalable cloud backends, custom operational dashboards, automated data pipelines, and workflow orchestration tailored for growing commercial enterprises.',
    features: [
      'Custom ERP, Billing & Telemetry API Integrations',
      'High-Availability Cloud Backends on Google Cloud Platform',
      'Granular Role-Based Access Control (RBAC) & Immutable Audit Trails',
      'Real-Time Operational Telemetry & Alert Webhooks',
      'Dedicated SLA & Tailored Enterprise Deployment Options'
    ],
    tags: ['Enterprise Architecture', 'Custom Workflows', 'API Integration', 'High-Availability Cloud', 'Dedicated SLA'],
    colSpan: 12,
    hasLiveDemo: false,
    actionText: 'Request Architecture Brief',
    previewType: 'custom-card'
  }
];

export const TECH_PILLARS = [
  {
    icon: '⚡',
    iconBg: '#eef2ff',
    iconColor: '#4f46e5',
    iconBorder: '#c7d2fe',
    title: 'High-Availability Cloud',
    subtitle: 'Google Cloud Platform (Asia South)',
    desc: 'Containerized microservices and multi-region database pipelines engineered for 99.9% target operational availability.',
    tags: ['Google Cloud', 'Asia South', 'Sub-150ms Sync']
  },
  {
    icon: '🔒',
    iconBg: '#ecfeff',
    iconColor: '#0891b2',
    iconBorder: '#a5f3fc',
    title: 'Encrypted Cloud Storage',
    subtitle: 'Standard Industry Data Security',
    desc: 'Data is stored with industry-standard AES-256 encryption at rest on Google Cloud Platform and transmitted over secure TLS 1.3 encrypted protocols.',
    tags: ['AES-256 at Rest', 'TLS 1.3 in Transit', 'Role-Based Access']
  },
  {
    icon: '📡',
    iconBg: '#eff6ff',
    iconColor: '#2563eb',
    iconBorder: '#bfdbfe',
    title: 'Real-Time Event Streams',
    subtitle: 'Cross-Device Instant Sync',
    desc: 'Cloud event listeners connecting clinic reception desks, doctor cabins, waiting hall TVs, and patient queues in real time.',
    tags: ['WebSocket Sync', 'Firestore Listeners', 'Cross-Device Updates']
  }
];

export const COCKPIT_TABS = [
  {
    id: 'rmedq',
    label: '🏥 Live Clinic OPD',
    badge: '🟢 Demonstration',
    cabin: 'Demo Cabin 2 • Dr. Rajesh Sharma',
    simSequence: [
      { token: '#A-14', patient: 'Demo Patient • 34y Female • General OPD', backlog: '3 Tokens in Queue (Simulated)', ai: '"Demonstration: Clinic queue velocity running smoothly. Predicted queue clearance at 12:45 PM. Zero patient backlog across all cabins."' },
      { token: '#A-15', patient: 'Demo Patient • 48y Male • Routine Follow-up', backlog: '2 Tokens in Queue (Simulated)', ai: '"Demonstration: Token #A-15 updated on live queue board. Patient checked in via reception QR code."' },
      { token: '#A-16', patient: 'Demo Patient • 29y Female • General Consultation', backlog: '1 Token in Queue (Simulated)', ai: '"Demonstration: Token #A-16 in waiting hall. Vitals recorded at reception desk."' }
    ]
  },
  {
    id: 'rmedq-hall',
    label: '🖥️ Waiting Hall Display',
    badge: '🟢 Live Display Telemetry',
    cabin: 'Main Waiting Hall • TV Display Stream',
    simSequence: [
      { token: 'CALLING: #A-14', patient: 'Cabin 2 • Dr. Rajesh Sharma', backlog: 'Next in Queue: #A-15 (Cabin 2), #B-04 (Cabin 1)', ai: '"Demonstration: Audio Chime broadcasted in waiting hall. Live token display updated across all synchronized reception monitors."' },
      { token: 'CALLING: #B-04', patient: 'Cabin 1 • Dr. Ananya Verma', backlog: 'Next in Queue: #A-15 (Cabin 2), #B-05 (Cabin 1)', ai: '"Demonstration: Live queue display stream synced via Google Cloud Firestore with real-time websocket latency < 200ms."' }
    ]
  }
];
